import { isErrorWithData } from '@/services/helpers';
import { useUpdateSalesRepMutation } from '@/services/salesReps';
import { Box, Button, Modal, TextareaInput, toast } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { SalesRep, SalesRepStatus } from 'types';

type TerminateSalesRepModalProps = {
  isOpen: boolean;
  salesRep: SalesRep | undefined;
  onClose: () => void;
};

interface FormValues {
  reason: string;
}

export const TerminateSalesRepModal = ({ isOpen, salesRep, onClose }: TerminateSalesRepModalProps) => {
  const [updateSalesRep, { isLoading }] = useUpdateSalesRepMutation();

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { reason: '' },
  });

  const handleClose = useCallback(() => {
    reset({ reason: '' });
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!salesRep) return;
      try {
        await updateSalesRep({
          id: salesRep.id,
          status: SalesRepStatus.terminated,
          reason: values.reason,
        }).unwrap();
        toast.success('Sales rep terminated');
        handleClose();
      } catch (error) {
        console.error(error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Failed to terminate sales rep. Please try again.');
        }
      }
    },
    [salesRep, updateSalesRep, handleClose],
  );

  if (!isOpen || !salesRep) return null;

  const repName = salesRep.idProviderUser?.displayName ?? 'this sales rep';

  return (
    <Modal ariaLabelledBy="terminateSalesRepTitle" isOpen={isOpen} onDismiss={handleClose} maxWidth="4xl">
      <Modal.Header id="terminateSalesRepTitle" title="Terminate Sales Rep" onDismiss={handleClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body background="secondary" childGap="lg">
          <Box as="p">Are you sure you want to terminate {repName}?</Box>
          <Controller
            name="reason"
            control={control}
            rules={{
              required: 'Reason is required',
              minLength: { value: 1, message: 'Reason is required' },
            }}
            render={({ field, fieldState }) => (
              <TextareaInput
                label="Reason"
                name={field.name}
                id="terminateSalesRep-reason"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                rows={3}
                error={fieldState.error?.message}
                isRequired
              />
            )}
          />
        </Modal.Body>
        <Modal.Footer justifyContent="space-between">
          <Button onClick={handleClose} variant="secondary" tone="neutral" isDisabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" tone="danger" isLoading={isLoading} isDisabled={isLoading}>
            Terminate
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
